import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';

// The website's pages by address, with the browser's own history: a link changes the address without loading the
// page again, Back and Forward work, and every page has an address a buyer can share or open from a QR code.

const CHANGE = 'grailfind:navigate';

function subscribe(onChange: () => void) {
  window.addEventListener('popstate', onChange);
  window.addEventListener(CHANGE, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener(CHANGE, onChange);
  };
}

const where = () => window.location.pathname + window.location.search;

/** The page's address: its path and its query, as "/s/kl?table=B12". */
export function useLocation(): string {
  return useSyncExternalStore(subscribe, where, () => '/');
}

/** Goes to `to` on this site. `replace` keeps Back from coming here again (a sign-in's return, a search). */
export function navigate(to: string, { replace = false }: { replace?: boolean } = {}) {
  if (to === where()) return;
  if (replace) window.history.replaceState(null, '', to);
  else window.history.pushState(null, '', to);
  window.dispatchEvent(new Event(CHANGE));
  const [, hash] = to.split('#');
  if (hash) requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ block: 'start' }));
  else window.scrollTo(0, 0);
}

/** A link on this site. A click with a modifier key, or with another button, opens it the browser's own way. */
export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(href);
  };
  return <a href={href} onClick={go} {...rest} />;
}

export type Route =
  | { page: 'shows'; query: URLSearchParams }
  | { page: 'show'; slug: string; query: URLSearchParams }
  | { page: 'card'; id: string }
  | { page: 'list' }
  | { page: 'account'; query: URLSearchParams }
  | { page: 'scan' }
  | { page: 'missing' };

/**
 * The page for an address. A sticker's QR code opens /i/<id>, also with capital letters (as the code holds them); the
 * site's first page is the shows.
 */
export function route(location: string): Route {
  const url = new URL(location, 'https://grailfind.invalid');
  const parts = url.pathname.split('/').filter(Boolean);
  const query = url.searchParams;

  if (parts.length === 0 || (parts.length === 1 && parts[0] === 'shows')) return { page: 'shows', query };
  if (parts.length === 2 && parts[0] === 's') return { page: 'show', slug: decodeURIComponent(parts[1]!), query };
  if (parts.length === 2 && (parts[0] === 'i' || parts[0] === 'I') && /^[a-z2-7]{26}$/i.test(parts[1]!))
    return { page: 'card', id: parts[1]!.toLowerCase() };
  if (parts.length === 1 && parts[0] === 'list') return { page: 'list' };
  if (parts[0] === 'account' && parts.length <= 2) return { page: 'account', query };
  if (parts.length === 1 && parts[0] === 'scan') return { page: 'scan' };
  return { page: 'missing' };
}
